//
// Copyright © 2026 Kotova GmbH.
//
// Helpers for phones: the on-screen keyboard, the bar with the applications at the bottom of the screen and the bar
// that iOS draws above the keyboard for the text fields of a page.
//

import { writable } from 'svelte/store'

// The visible part of the page has to be this much lower than the page itself to count as a keyboard.
// The toolbars of the browser change the height by less than that.
const keyboardMinHeight = 150

/**
 * True while the on-screen keyboard covers a part of the page. It is only tracked on phones, see trackKeyboard.
 * @public
 */
export const keyboardVisibleStore = writable<boolean>(false)

let stopKeyboardTracking: (() => void) | undefined

/**
 * Starts or stops watching the on-screen keyboard. The browser has no event for it, so it is derived from the size
 * of the visual viewport: iOS and current Chrome do not resize the page for the keyboard, they only shrink the part
 * of it that is visible. Browsers that resize the page instead are noticed by the page getting much lower than it was.
 * @public
 */
export function trackKeyboard (enabled: boolean): void {
  if (!enabled) {
    stopKeyboardTracking?.()
    stopKeyboardTracking = undefined
    keyboardVisibleStore.set(false)
    return
  }
  if (stopKeyboardTracking !== undefined || typeof window === 'undefined') return

  const viewport = window.visualViewport ?? undefined
  let pageWidth = window.innerWidth
  // The highest the page has been since the device was last rotated
  let pageHeight = window.innerHeight
  let visible = false

  const update = (): void => {
    if (window.innerWidth !== pageWidth) {
      pageWidth = window.innerWidth
      pageHeight = window.innerHeight
    } else if (!visible) {
      pageHeight = Math.max(pageHeight, window.innerHeight)
    }
    // A page that is zoomed in shows less of itself, but there is no keyboard then
    const zoomed = (viewport?.scale ?? 1) > 1.01
    const next = !zoomed && pageHeight - (viewport?.height ?? window.innerHeight) > keyboardMinHeight
    if (next === visible) return
    visible = next
    keyboardVisibleStore.set(next)
    // Safari can leave the page scrolled up after the keyboard has gone
    if (!next && (viewport?.offsetTop ?? 0) > 0) window.scrollTo(0, 0)
  }

  viewport?.addEventListener('resize', update)
  window.addEventListener('resize', update)
  update()

  stopKeyboardTracking = () => {
    viewport?.removeEventListener('resize', update)
    window.removeEventListener('resize', update)
  }
}

const bottomBarHiders = new Set<object>()

/**
 * True while at least one view asks for the bar with the applications at the bottom of the screen to get out of
 * the way. Only the phone layout has such a bar.
 * @public
 */
export const bottomBarHiddenStore = writable<boolean>(false)

/**
 * Asks to hide the bar with the applications at the bottom of the screen, or to show it again. The bar stays hidden
 * as long as there is a view that wants it hidden, so every view passes itself as the owner of its own request.
 * @public
 */
export function setBottomBarHidden (owner: object, hidden: boolean): void {
  if (bottomBarHiders.has(owner) === hidden) return
  if (hidden) {
    bottomBarHiders.add(owner)
  } else {
    bottomBarHiders.delete(owner)
  }
  bottomBarHiddenStore.set(bottomBarHiders.size > 0)
}

const nonTextInputTypes = 'hidden checkbox radio button submit reset image file range color'.split(' ')
const textFieldSelector = [
  `input${nonTextInputTypes.map((type) => `:not([type=${type}])`).join('')}`,
  'textarea',
  'select',
  '[contenteditable]:not([contenteditable=false])'
].join(',')

/**
 * Takes all text fields except the given one out of the tab order and returns the function that puts them back.
 *
 * iOS draws a bar with the arrows "previous" and "next" above the keyboard. A web page cannot hide that bar, but the
 * arrows are greyed out when there is no other text field to jump to, and only fields in the tab order count.
 * Call it when the field gets the focus and the returned function when it loses it. A phone has no use for the tab
 * order, so do not use it elsewhere.
 * @public
 */
export function isolateTextField (keep: HTMLElement): () => void {
  const changed: Array<{ element: HTMLElement, tabindex: string | null }> = []
  document.querySelectorAll<HTMLElement>(textFieldSelector).forEach((element) => {
    if (element.tabIndex < 0 || keep.contains(element) || element.contains(keep)) return
    changed.push({ element, tabindex: element.getAttribute('tabindex') })
    element.setAttribute('tabindex', '-1')
  })

  return () => {
    for (const { element, tabindex } of changed) {
      if (tabindex === null) {
        element.removeAttribute('tabindex')
      } else {
        element.setAttribute('tabindex', tabindex)
      }
    }
  }
}
